import { json, type LoaderFunction } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { Spacer } from '#app/components/spacer.tsx'
import { RssIcon } from '#app/components/ui/icons/index.tsx'
import { getBlogMdxListItems } from '#app/utils/mdx.ts'

const POSTS_PER_PAGE = 15

type LoaderData = {
	posts: Array<{
		title: string
		description: string
		slug: string
		image: string
	}>
	currentPage: number
	totalPages: number
}

export const loader: LoaderFunction = async ({ request }) => {
	const url = new URL(request.url)
	const requestedPage = Number(url.searchParams.get('page') ?? 1)
	const posts = await getBlogMdxListItems({ request }).then(allPosts =>
		Promise.all(
			allPosts.map(async p => ({
				title: p.frontmatter.title ?? 'Untitled',
				description: p.frontmatter.description ?? 'No description',
				image: p.frontmatter.bannerUrl ?? '',
				slug: p.slug,
			})),
		),
	)
	const totalPages = Math.max(1, Math.ceil(posts.length / POSTS_PER_PAGE))
	const normalizedPage = Number.isInteger(requestedPage) ? requestedPage : 1
	const currentPage = Math.min(
		Math.max(1, Number.isFinite(normalizedPage) ? normalizedPage : 1),
		totalPages,
	)
	const start = (currentPage - 1) * POSTS_PER_PAGE
	const paginatedPosts = posts.slice(start, start + POSTS_PER_PAGE)

	const data: LoaderData = { posts: paginatedPosts, currentPage, totalPages }

	return json(data, {
		headers: {
			'Cache-Control': 'private, max-age=60',
			Vary: 'Cookie',
		},
	})
}

export default function BlogList() {
	const data = useLoaderData<LoaderData>()

	return (
		<div className="container">
			<Spacer size="lg" />
			<main>
				<div id="posts">
					<div className="flex flex-row">
						<h1>Posts</h1>
						<Link
							reloadDocument
							to="rss.xml"
							className="underlined hover:text-team-current focus:text-team-current ml-2 inline-block text-primary"
						>
							<RssIcon title="Get my blog as RSS" />
						</Link>
					</div>
					<Spacer size="2xs" />
					<div className="md:flex-column flex flex-row gap-4">
						<img
							className="flex-[1] rounded-md border md:max-w-[40%]"
							alt="face swap with midjourney example"
							src="https://luisiacc-swapmyface-public.s3.amazonaws.com/mid.webp"
						/>
						<div className="flex-[1]">
							<h2>How to use SwapMyFace with MidJourney</h2>
							<p className="mt-2 text-gray-500">
								Discover the easiest way to put your face into any picture that
								is generated through midjourney, in just 3 clicks
							</p>
							<Link
								to="/articles/how-to-use-with-midjourney"
								className="mt-3 inline-block text-indigo-400 hover:text-indigo-500"
							>
								Read More
							</Link>
						</div>
					</div>
					<Spacer size="lg" />
					<div className="grid grid-cols-1 gap-10 md:grid-cols-3">
						{data.posts.map(post => (
							<BlogCard key={post.slug} post={post} />
						))}
						<Spacer size="3xs" />
					</div>
					{data.totalPages > 1 ? (
						<Pagination
							currentPage={data.currentPage}
							totalPages={data.totalPages}
						/>
					) : null}
				</div>
			</main>
		</div>
	)
}

function getPageUrl(page: number) {
	return page === 1 ? '/blog' : `/blog?page=${page}`
}

function Pagination({
	currentPage,
	totalPages,
}: {
	currentPage: number
	totalPages: number
}) {
	const canGoPrevious = currentPage > 1
	const canGoNext = currentPage < totalPages

	return (
		<nav
			className="mt-10 flex flex-wrap items-center justify-center gap-2"
			aria-label="Blog pagination"
		>
			{canGoPrevious ? (
				<Link
					to={getPageUrl(currentPage - 1)}
					className="rounded-md border px-3 py-2 text-sm text-foreground/80 hover:bg-foreground/5"
				>
					Previous
				</Link>
			) : (
				<span className="rounded-md border px-3 py-2 text-sm text-foreground/40">
					Previous
				</span>
			)}
			{Array.from({ length: totalPages }, (_, index) => {
				const page = index + 1
				const isCurrentPage = page === currentPage

				return (
					<Link
						key={page}
						to={getPageUrl(page)}
						aria-current={isCurrentPage ? 'page' : undefined}
						className={`rounded-md border px-3 py-2 text-sm ${
							isCurrentPage
								? 'bg-indigo-500 text-white'
								: 'text-foreground/80 hover:bg-foreground/5'
						}`}
					>
						{page}
					</Link>
				)
			})}
			{canGoNext ? (
				<Link
					to={getPageUrl(currentPage + 1)}
					className="rounded-md border px-3 py-2 text-sm text-foreground/80 hover:bg-foreground/5"
				>
					Next
				</Link>
			) : (
				<span className="rounded-md border px-3 py-2 text-sm text-foreground/40">
					Next
				</span>
			)}
		</nav>
	)
}

function BlogCard({ post }: { post: LoaderData['posts'][number] }) {
	return (
		<Link
			to={'/blog/' + post.slug}
			className="mx-auto max-w-3xl overflow-hidden rounded-xl bg-gray-50 transition-all hover:shadow-lg dark:bg-[#212121] md:max-w-2xl"
		>
			<div className="md:h-full">
				<div className="flex-none md:flex-[1] md:flex-shrink-0">
					<img
						className="h-full w-full object-cover"
						src={post.image}
						alt={post.title}
					/>
				</div>
				<div className="p-8 md:flex-[1]">
					<div className="text-lg font-semibold tracking-wide text-indigo-500">
						{post.title}
					</div>
					<p className="mt-2 text-sm text-foreground/60 ">{post.description}</p>
					<Link
						to={'/blog/' + post.slug}
						className="mt-3 inline-block text-indigo-400 hover:text-indigo-500"
					>
						Read More
					</Link>
				</div>
			</div>
		</Link>
	)
}
